import { createMultifileUploadSlice } from "@nova/slice-multifile-upload"
import { withRoot } from "@nova/with-root"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import { AnyAction, ImageProcessingState, ImageWarnings, JobStatusType } from "@nova/constants"
import { colorFinderWarningProcessor } from "./ColorFinderWarnings"
import { get, memoize } from "nodash"
import { selectVideoColorFinderFlightById } from "features/entity-caching/flights/selectors"
import { createIdFromFile } from "@nova/util-strings"
import { addFlightUsage } from "features/data-management/workspace/subscriptions/module"
import { dateAsNumber } from "@nova/serializer/src/client/util"

type ColorFinderMeta = {
    organizationID: string
    fireID: string
    flightID: string
    setID: string
    file: File
}

export type ColorFinderState = {
    id: string
    name: string
    s3Key?: string
    state: ImageProcessingState
    warnings?: ImageWarnings
    deletedAt?: string
    isSRT?: boolean
    file?: File
    takenAt?: number
}

const key = ({ organizationID, fireID, flightID, file }: ColorFinderMeta) => {
    const sanitizedFileName = file.name.toLowerCase().includes(".mp4") ? file.name : "metadata.srt"
    return `public/org/${organizationID}/fire/${fireID}/flight/${flightID}/video/${sanitizedFileName}`
}

const {
    actions,
    reducer: imagesReducer,
    selectors: {
        selectSet,
        selectUploadsForSet,
        selectUploadIdsForSet,
        selectCancelledUploadIdsForSet,
        selectUploadsForSetSummary: baseSelectUploadsForSetSummary,
        selectPendingForSet: baseSelectPendingForSet,
    },
} = createMultifileUploadSlice<"colorFinder-v1", ColorFinderState, ColorFinderMeta>(
    "colorFinder-v1",
    (m: ColorFinderMeta) => {
        const isSRT = !m.file.name.toLowerCase().includes(".mp4")
        const lastModifiedDate = get(m.file, "lastModifiedDate")
        return {
            id: createIdFromFile(m.file),
            name: m.file.name,
            state: ImageProcessingState.Pending,
            file: m.file,
            isSRT,
            takenAt: dateAsNumber(lastModifiedDate as string | Date | undefined),
        } satisfies ColorFinderState
    },
    {
        addUploadToProject: null,
    },
    async (state, meta, update) => {
        const s3Key = key(meta)
        state = {
            ...state,
            s3Key,
            state: ImageProcessingState.ProcessingDone,
        }
        update(state)
        return state
    },
    colorFinderWarningProcessor,
    false
)

export const stageFiles = (
    organizationID: string,
    fireID: string,
    flightID: string,
    files: File[],
    onFinish?: () => void
) => {
    const meta = new Array<ColorFinderMeta>(files.length)
    let totalSize = 0
    for (let i = 0; i < files.length; i++) {
        meta[i] = {
            organizationID,
            fireID,
            flightID,
            setID: flightID,
            file: files[i],
        }
        totalSize += files[i].size
    }
    addFlightUsage(flightID, totalSize)
    return actions.stageFiles({ meta, onFinish })
}

export const uploadFlight = (id: string) => actions.signal("upload", id)
export const deleteUpload = actions.deleteUpload
export const deleteUploadSet = actions.deleteUploadSet

export const selectVideoColorFinderUploads = memoize((id: string) =>
    createSelector(
        [selectVideoColorFinderFlightById(id), selectUploadsForSet(id)],
        (flight, uploads) => {
            if (flight?.job && !uploads?.length) {
                const files: ColorFinderState[] = [
                    {
                        id: "mock-video-file-id",
                        name: flight.job.videoPath.split("/").at(-1) || "video.mp4",
                        state: ImageProcessingState.UploadDone,
                    },
                ]
                if (flight.job.srtPath) {
                    files.push({
                        id: "srt-file-id",
                        name: flight.job.srtPath.split("/").at(-1) || "metadata.srt",
                        state: ImageProcessingState.UploadDone,
                        isSRT: true,
                    })
                }
                return files
            } else return uploads
        }
    )
)

// this is summarizing the state of uploads for a set
// it's useful for determining if a set is uploading, how much of it is uploading, etc.
const selectUploadsForSetSummary = (id: string) =>
    baseSelectUploadsForSetSummary(
        id,
        (t) => t.state,
        (t) => t.state === ImageProcessingState.UploadDone
    )

const selectPendingUploadsForSet = (id: string) => baseSelectPendingForSet(id, "upload")

export const selectVideoColorFinderJobByFlightId = memoize((id: string) =>
    createSelector(
        [selectVideoColorFinderFlightById(id), selectMockVideoColorFinderJobByFlightId(id)],
        (flight, mockJob) => (mockJob as MockVideoColorFinderJob | undefined) || flight?.job
    )
)

type MockVideoColorFinderJob = {
    id: string
    mock: true
    upload: boolean
    name: string
    status: JobStatusType
}

type UIState = {
    mockJobs: Record<string, MockVideoColorFinderJob | undefined>
    playing: boolean
    splitButtonOpen: boolean
    statusCheckerOpen: boolean
    selectedUpload: Record<string, boolean>
}

const uiStateName = "colorFinder-ui-v1"
const initialUIState = {
    mockJobs: {},
    playing: false,
    splitButtonOpen: false,
    selectedUpload: {},
    statusCheckerOpen: false,
}

const {
    reducer: uiStateReducer,
    actions: {
        setSplitButtonOpen,
        setStatusCheckerOpen,
        setSelectedUpload,
        createMockVideoColorFinderJob,
        deleteMockVideoColorFinderJob,
    },
} = createSlice({
    name: uiStateName,
    initialState: { ...initialUIState } as UIState,
    reducers: {
        createMockVideoColorFinderJob: (
            state,
            { payload }: { payload: MockVideoColorFinderJob }
        ) => {
            state.mockJobs[payload.id] = payload
        },
        deleteMockVideoColorFinderJob: (state, action: { payload: string }) => {
            delete state.mockJobs[action.payload]
        },
        setPlaying: (state, action: { payload: boolean }) => {
            state.playing = action.payload
        },
        setSplitButtonOpen: (state, action) => {
            state.splitButtonOpen = action.payload
        },
        setStatusCheckerOpen: (state, action) => {
            state.statusCheckerOpen = action.payload
        },
        setSelectedUpload: (state, action) => {
            state.selectedUpload = action.payload
        },
    },
})

const {
    selectSplitButtonOpen,
    selectSelectedUpload,
    selectStatusCheckerOpen,
    selectMockVideoColorFinderJobByFlightId,
} = withRoot((r: AnyAction) => r[uiStateName], {
    selectPlaying: () => (s) => s.playing,
    selectSplitButtonOpen: () => (s) => s.splitButtonOpen,
    selectStatusCheckerOpen: () => (s) => s.statusCheckerOpen,
    selectSelectedUpload: () => (s) => s.selectedUpload,
    selectMockVideoColorFinderJobByFlightId: (id: string) => (s) => s.mockJobs[id],
})

// export selectors
export {
    selectSet,
    selectUploadsForSet,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
    selectPendingUploadsForSet,
    selectUploadsForSetSummary,
    selectSplitButtonOpen,
    selectStatusCheckerOpen,
    selectSelectedUpload,
}

// export actions
export {
    setSplitButtonOpen,
    setStatusCheckerOpen,
    setSelectedUpload,
    createMockVideoColorFinderJob,
    deleteMockVideoColorFinderJob,
}

// export reducer
export const reducer = {
    ...imagesReducer,
}

export const uiReducer = { [uiStateName]: uiStateReducer }
